import React, { ReactNode, useCallback, useEffect, useLayoutEffect, useRef } from 'react'

import { BottomSheet } from '@interco/inter-ui/components/BottomSheet'

import * as S from './styles'
import { LabelTitleHeadline3 } from '../Labels'

const SWIPE_CLOSE_DELTA = 100
const OVERLAY_FADE_DISTANCE = 280
const SHEET_DISMISS_DISTANCE = 1000
const SHEET_DISMISS_MS = 280
const OVERLAY_TEST_ID = 'bottom-sheet-overlay'
const SHEET_TEST_ID = 'bottom-sheet-container'
const TOUCH_LOCK_STYLE_ID = 'bottom-sheet-confirm-touch-lock'
const RGBA_PATTERN = /rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\)/

let touchLockUsers = 0

const ensureTouchLockStyle = () => {
  touchLockUsers += 1
  if (document.getElementById(TOUCH_LOCK_STYLE_ID)) return

  const tag = document.createElement('style')
  tag.id = TOUCH_LOCK_STYLE_ID
  tag.textContent = `[data-testid="${SHEET_TEST_ID}"] { touch-action: none; }`
  document.head.appendChild(tag)
}

const removeTouchLockStyle = () => {
  touchLockUsers = Math.max(0, touchLockUsers - 1)
  if (touchLockUsers === 0) document.getElementById(TOUCH_LOCK_STYLE_ID)?.remove()
}

const overlayForSheet = (sheet: HTMLElement): HTMLElement | null =>
  sheet.closest(`[data-testid="${OVERLAY_TEST_ID}"]`) as HTMLElement | null

const sheetFromAnchor = (anchor: HTMLElement | null): HTMLElement | null =>
  anchor?.closest(`[data-testid="${SHEET_TEST_ID}"]`) as HTMLElement | null

const dragOffset = (deltaY: number): number => Math.max(0, deltaY)

const parseRgba = (color: string): [number, number, number, number] => {
  const match = RGBA_PATTERN.exec(color)
  if (!match) return [0, 0, 0, 0.64]

  return [
    Number(match[1]),
    Number(match[2]),
    Number(match[3]),
    match[4] === undefined ? 1 : Number(match[4]),
  ]
}

const getBaseOverlayBackground = (overlay: HTMLElement): string => {
  if (overlay.dataset.baseBg) return overlay.dataset.baseBg

  const computed = getComputedStyle(overlay).backgroundColor
  overlay.dataset.baseBg =
    computed && computed !== 'rgba(0, 0, 0, 0)' ? computed : 'rgba(0, 0, 0, 0.64)'

  return overlay.dataset.baseBg
}

const clearPortalInlineStyles = (overlay: HTMLElement | null, sheet: HTMLElement | null) => {
  if (overlay) {
    delete overlay.dataset.baseBg
    overlay.style.removeProperty('transition')
    overlay.style.removeProperty('opacity')
    overlay.style.removeProperty('background')
  }

  if (!sheet) return

  sheet.style.removeProperty('transition')
  sheet.style.removeProperty('bottom')
}

const dimOverlay = (overlay: HTMLElement | null, deltaY: number) => {
  if (!overlay) return

  const progress = Math.min(1, dragOffset(deltaY) / OVERLAY_FADE_DISTANCE)
  const [red, green, blue, alpha] = parseRgba(getBaseOverlayBackground(overlay))
  overlay.style.transition = 'none'
  overlay.style.opacity = '1'
  overlay.style.background = `rgba(${red}, ${green}, ${blue}, ${alpha * (1 - progress)})`
}

const moveSheet = (sheet: HTMLElement | null, deltaY: number) => {
  if (!sheet) return

  sheet.style.transition = 'none'
  sheet.style.bottom = `-${dragOffset(deltaY)}px`
}

const applyDrag = (overlay: HTMLElement | null, sheet: HTMLElement | null, deltaY: number) => {
  moveSheet(sheet, deltaY)
  dimOverlay(overlay, deltaY)
}

const dismissSheet = (overlay: HTMLElement | null, sheet: HTMLElement | null) => {
  if (sheet) {
    sheet.style.transition = `bottom ${SHEET_DISMISS_MS}ms cubic-bezier(0.165, 0.84, 0.44, 1)`
    sheet.style.bottom = `-${SHEET_DISMISS_DISTANCE}px`
  }

  dimOverlay(overlay, OVERLAY_FADE_DISTANCE)
  if (overlay) overlay.style.transition = `background ${SHEET_DISMISS_MS}ms ease`
}

const restoreOverlay = (overlay: HTMLElement | null, sheet: HTMLElement | null) => {
  if (overlay) {
    overlay.style.transition = 'background 0.15s ease'
    overlay.style.opacity = '1'
    overlay.style.background =
      overlay.dataset.baseBg || 'var(--bottom-sheet-overlay-background-color)'
  }

  if (!sheet) return

  sheet.style.transition = 'bottom 0.15s ease'
  sheet.style.bottom = '0px'
}

const isSheetGestureTarget = (target: EventTarget | null, sheet: HTMLElement | null): boolean => {
  if (!(target instanceof Element) || !sheet) return false
  if (target.closest('button')) return false

  return sheet.contains(target)
}

const firstTouchClientY = (touches: TouchList): number | undefined => touches[0]?.clientY

export type BottomSheetConfirmProps = {
  visible: boolean
  title: string
  info?: string
  textContinue: string
  textCancel?: string
  closeModal: () => void
  onContinue: () => void
  onCancel?: () => void
  children?: ReactNode
}
export const BottomSheetConfirm = ({
  visible,
  title,
  info,
  textContinue,
  textCancel,
  closeModal,
  onContinue,
  onCancel,
  children,
}: BottomSheetConfirmProps) => {
  const startY = useRef(0)
  const isDragging = useRef(false)
  const closeModalRef = useRef(closeModal)
  const dismissTimerRef = useRef<number>()
  const contentRef = useRef<HTMLDivElement | null>(null)
  const overlayRef = useRef<HTMLElement | null>(null)
  const sheetRef = useRef<HTMLElement | null>(null)

  closeModalRef.current = closeModal

  const bindPortalNodes = useCallback(() => {
    const sheet = sheetFromAnchor(contentRef.current)
    sheetRef.current = sheet
    overlayRef.current = sheet ? overlayForSheet(sheet) : null
  }, [])

  useLayoutEffect(() => {
    if (!visible) {
      overlayRef.current = null
      sheetRef.current = null
      return
    }

    bindPortalNodes()
    clearPortalInlineStyles(overlayRef.current, sheetRef.current)
  }, [visible, bindPortalNodes])

  const closeWithAnimation = useCallback(() => {
    if (dismissTimerRef.current !== undefined) return
    isDragging.current = false
    bindPortalNodes()
    dismissSheet(overlayRef.current, sheetRef.current)
    dismissTimerRef.current = window.setTimeout(() => {
      dismissTimerRef.current = undefined
      clearPortalInlineStyles(overlayRef.current, sheetRef.current)
      closeModalRef.current()
    }, SHEET_DISMISS_MS)
  }, [bindPortalNodes])

  useEffect(() => {
    if (!visible) return undefined

    const clearDismissTimer = () => {
      if (dismissTimerRef.current === undefined) return
      window.clearTimeout(dismissTimerRef.current)
      dismissTimerRef.current = undefined
    }

    const beginGesture = (clientY: number, target: EventTarget | null) => {
      if (dismissTimerRef.current !== undefined) return
      bindPortalNodes()
      if (!isSheetGestureTarget(target, sheetRef.current)) return
      isDragging.current = true
      startY.current = clientY
    }

    const moveGesture = (clientY: number) => {
      if (!isDragging.current) return
      applyDrag(overlayRef.current, sheetRef.current, clientY - startY.current)
    }

    const endGesture = (clientY: number) => {
      if (!isDragging.current) return
      isDragging.current = false

      const deltaY = clientY - startY.current
      if (deltaY > SWIPE_CLOSE_DELTA) {
        closeWithAnimation()
        return
      }

      restoreOverlay(overlayRef.current, sheetRef.current)
    }

    const cancelGesture = () => {
      if (!isDragging.current) return
      isDragging.current = false
      restoreOverlay(overlayRef.current, sheetRef.current)
    }

    const onTouchStart = (event: TouchEvent) => {
      const clientY = firstTouchClientY(event.touches)
      if (clientY === undefined) return
      beginGesture(clientY, event.target)
    }
    const onTouchMove = (event: TouchEvent) => {
      const clientY = firstTouchClientY(event.touches)
      if (clientY === undefined) return
      moveGesture(clientY)
    }
    const onTouchEnd = (event: TouchEvent) => {
      const clientY = firstTouchClientY(event.changedTouches)
      if (clientY === undefined) {
        cancelGesture()
        return
      }
      endGesture(clientY)
    }
    const onTouchCancel = () => {
      cancelGesture()
    }
    const onMouseDown = (event: MouseEvent) => {
      beginGesture(event.clientY, event.target)
    }
    const onMouseMove = (event: MouseEvent) => {
      if (!(event.buttons & 1)) {
        if (isDragging.current) cancelGesture()
        return
      }
      moveGesture(event.clientY)
    }
    const onMouseUp = (event: MouseEvent) => {
      endGesture(event.clientY)
    }

    ensureTouchLockStyle()

    document.addEventListener('touchstart', onTouchStart, true)
    document.addEventListener('touchmove', onTouchMove, true)
    document.addEventListener('touchend', onTouchEnd, true)
    document.addEventListener('touchcancel', onTouchCancel, true)
    document.addEventListener('mousedown', onMouseDown, true)
    document.addEventListener('mousemove', onMouseMove, true)
    document.addEventListener('mouseup', onMouseUp, true)

    return () => {
      cancelGesture()
      clearDismissTimer()
      removeTouchLockStyle()
      document.removeEventListener('touchstart', onTouchStart, true)
      document.removeEventListener('touchmove', onTouchMove, true)
      document.removeEventListener('touchend', onTouchEnd, true)
      document.removeEventListener('touchcancel', onTouchCancel, true)
      document.removeEventListener('mousedown', onMouseDown, true)
      document.removeEventListener('mousemove', onMouseMove, true)
      document.removeEventListener('mouseup', onMouseUp, true)
    }
  }, [visible, closeWithAnimation, bindPortalNodes])

  if (!visible) return <></>

  return (
    <BottomSheet noSwipe onClose={closeWithAnimation} data-testid="BottomSheet">
      <div ref={contentRef} style={{ display: 'contents' }}>
        <LabelTitleHeadline3>{title}</LabelTitleHeadline3>
        <S.LabelNameCompany>
          {info && <S.Label data-testid="info">{info}</S.Label>}
          {children}
        </S.LabelNameCompany>
        <S.Button onClick={onContinue}>{textContinue}</S.Button>
        {textCancel && <S.ButtonSecondary onClick={onCancel}>{textCancel}</S.ButtonSecondary>}
      </div>
    </BottomSheet>
  )
}
